import { useEffect, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';
import toast from 'react-hot-toast';
import Widget from 'components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import TextButton from 'components/inputs/TextButton';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetHRSpeedingReport } from 'api/kpi/kpi';
import { useExportExportHRSpeedingReportHook } from 'api/export/export';
import type { KPIHRSpeedingDataPoint } from 'api/model';
import { HRSpeedingWidgetsEnum, LongTitles } from '../utils';

type SortField = 'fieldTesterName' | 'date' | 'groupName';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
};

const headerStyle: React.CSSProperties = {
  padding: '8px',
  textAlign: 'left',
  borderBottom: '2px solid #ddd',
};

const cellStyle: React.CSSProperties = {
  padding: '8px',
  verticalAlign: 'top',
};

// Replace tabs/newlines so a value never breaks out of its cell when pasted
// as tab-separated values into Excel / Google Sheets.
const sanitizeCell = (value: string): string =>
  value.replace(/\r?\n/g, '; ').replace(/\t/g, ' ');

const buildRowTsv = (dp: KPIHRSpeedingDataPoint, violation: string): string =>
  [
    dp.registrationNumbers,
    dp.fieldTesterName,
    formatDate(new Date(dp.date)),
    violation,
    dp.comment ?? '',
    dp.groupLeaderName ?? '',
    dp.groupName ?? '',
  ]
    .map((v) => sanitizeCell(v ?? ''))
    .join('\t');

const HRSpeedingWidget = () => {
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);
  const [, copy] = useCopyToClipboard();

  const {
    dashboardState: { daterange },
  } = useDashboardControls();

  const getReport = useKPIGetHRSpeedingReport();
  const downloadExcel = useExportExportHRSpeedingReportHook();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const startMs = startDate ? startDate.getTime() : null;
  const endMs = endDate ? endDate.getTime() : null;

  useEffect(() => {
    if (startDate && endDate) {
      getReport.mutate({
        data: {
          startDate,
          endDate,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMs, endMs]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: KPIHRSpeedingDataPoint[]
  ): KPIHRSpeedingDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'fieldTesterName':
          aVal = a.fieldTesterName || '';
          bVal = b.fieldTesterName || '';
          break;
        case 'groupName':
          aVal = a.groupName || '';
          bVal = b.groupName || '';
          break;
        case 'date':
        default:
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
          break;
      }
      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  };

  const data = getReport.data?.dataPoints || [];
  const sorted = sortData(data);

  return (
    <Widget
      title={LongTitles[HRSpeedingWidgetsEnum.SpeedingReport]}
      count={undefined}
      isLoading={getReport.isLoading}
      info="Visar bil-uppdrag där FT körde över 120 km/h eller hade GPS av i mer än globalt tröskelvärde av uppdragstiden. Röd rad = max-hastighet över 140 km/h."
    >
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={isDownloading || !startDate || !endDate}
          onClick={async () => {
            if (!startDate || !endDate) return;
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate,
                endDate,
              });
              if (result) {
                downloadBlob(
                  result,
                  `HR_HastighetGPS_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner Excel
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getReport.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Inga överträdelser för vald period.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th style={headerStyle}>Regnr</th>
                <th
                  onClick={() => handleSort('fieldTesterName')}
                  style={{ ...headerStyle, cursor: 'pointer' }}
                >
                  FT Namn
                  <SortIndicator
                    field="fieldTesterName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('date')}
                  style={{ ...headerStyle, cursor: 'pointer' }}
                >
                  Datum
                  <SortIndicator
                    field="date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th style={headerStyle}>Överträdelse</th>
                <th style={headerStyle}>Kommentar</th>
                <th style={headerStyle}>Gruppledare</th>
                <th
                  onClick={() => handleSort('groupName')}
                  style={{ ...headerStyle, cursor: 'pointer' }}
                >
                  Grupp
                  <SortIndicator
                    field="groupName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th style={headerStyle} aria-label="Åtgärder" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((dp, index) => {
                const violation =
                  dp.hasSpeedingViolation && dp.hasGpsCoverageViolation
                    ? 'Hastighet + GPS'
                    : dp.hasSpeedingViolation
                      ? 'Hastighet'
                      : 'GPS ej påslagen';
                const rowStyle: React.CSSProperties = {
                  borderBottom: '1px solid #eee',
                  backgroundColor: dp.hasExtremeSpeed ? '#FFCCCC' : undefined,
                };
                return (
                  <tr
                    key={`${dp.fieldTesterId}-${dp.date}-${index}`}
                    style={rowStyle}
                  >
                    <td style={cellStyle}>{dp.registrationNumbers}</td>
                    <td style={cellStyle}>{dp.fieldTesterName}</td>
                    <td style={cellStyle}>
                      {formatDate(new Date(dp.date))}
                    </td>
                    <td style={cellStyle}>{violation}</td>
                    <td style={{ ...cellStyle, whiteSpace: 'pre-line' }}>
                      {dp.comment}
                    </td>
                    <td style={cellStyle}>{dp.groupLeaderName ?? ''}</td>
                    <td style={cellStyle}>{dp.groupName ?? ''}</td>
                    <td style={{ ...cellStyle, textAlign: 'right' }}>
                      <TextButton
                        onClick={async () => {
                          await copy(buildRowTsv(dp, violation));
                          toast('Rad kopierad till urklipp', {
                            duration: 2000,
                          });
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faClone}
                          title="Kopiera rad till urklipp"
                        />
                      </TextButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default HRSpeedingWidget;
